// src/services/api.js
import axios from 'axios';
import { pushTrackingEvent, trackBookingResult, trackPayoutResult, decimalAnalyticsValue } from '../utils/analytics';

// Base URL (the application is built by CRA/CRACO; SSR/tests may omit process.env)
const apiBase = (() => {
  const envBase =
    typeof process !== 'undefined' && process.env
      ? process.env.REACT_APP_API_BASE
      : undefined;

  if (envBase && envBase !== '/' && envBase !== '') return envBase;

  // 2. Default logic: always use /api prefix to avoid routing conflicts with frontend pages
  return '/api';
})();

if (process.env.NODE_ENV !== 'production' || true) {
  // console.log('[API URL CONFIG]', apiBase);
}

export const apiClient = axios.create({
  baseURL: apiBase,
  withCredentials: true,
  timeout: 20000,
});

// Request interceptor to add the JWT token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// --- enkel fejl-normalisering ---
const normalizeError = (error) => {
  const status = error?.response?.status;
  const code = error?.response?.data?.error;
  const goodsErrors={goods_exceed_vehicle_capacity:'Varerne overstiger den valgte bils kapacitet. Vælg en større bil eller bed om et tilbud.',vehicle_capacity_not_configured:'Kapaciteten for denne bil er ikke konfigureret. Kontakt os for et tilbud.',goods_exceed_configured_volume:'Mængden overstiger grænsen for Instant Booking. Bed om et tilbud.',handling_exceeds_configured_limit:'Håndteringstiden overstiger den konfigurerede Instant-grænse. Bed om et tilbud.',goods_exceed_pallet_weight:'En varelinje overstiger pallens tilladte vægt pr. enhed.',invalid_goods_quantity:'Antal skal være et positivt helt tal.'};
  if(goodsErrors[code])return goodsErrors[code];

  const message =
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    'Noget gik galt';

  if (status === 401) {
    return 'Session udløb – log ind igen.';
  }

  if (status === 403) {
    // Mere specifikke beskeder for kendte backend-koder
    if (code === 'forbidden_not_carrier') {
      return 'Kun godkendte vognmænd kan byde på opgaver.';
    }
    if (code === 'carrier_not_approved') {
      return 'Din vognmandsprofil er endnu ikke godkendt. Kontakt administrationen for at blive godkendt.';
    }
    if (code === 'user_blocked') {
      return 'Din konto er blokeret. Kontakt supporten.';
    }
    if (code === 'insurance_required_or_expired') {
      return 'Du kan ikke afgive bud, fordi din forsikring mangler eller er udløbet.';
    }
    if (code === 'negative_balance') {
      return 'Du har en negativ saldo og kan ikke afgive bud før saldoen er udlignet.';
    }
    if (code === 'max_bids_reached') {
      return 'Denne opgave har allerede nået det maksimale antal bud.';
    }
    return 'Du har ikke adgang til denne handling.';
  }

  if (status === 409) {
    if (code === 'task_closed') {
      return 'Opgaven er lukket for nye bud.';
    }
    if (code === 'bid_locked') {
      return 'Dette bud kan ikke ændres, fordi det allerede er behandlet.';
    }
    if (code === 'valid_until_in_past') {
      return 'Gyldighedsdatoen for buddet ligger i fortiden.';
    }
  }

  return message;
};

const handle = (p) => p.then(r => r.data).catch(e => { throw new Error(normalizeError(e)); });
const submissionRequest = p => p.then(r=>r.data).catch(e=>{
  const code=e?.response?.data?.error||'submission_request_failed';
  const retryAt=e?.response?.data?.retryAt;
  const wait=['submission_mail_limit','submission_resend_wait'].includes(code)&&typeof retryAt==='string'&&Number.isFinite(Date.parse(retryAt));
  throw Object.assign(new Error(code),{status:e?.response?.status,data:{error:code,...(wait?{retryAt}:{})}});
});

// --- små helpers (med signal/headers) ---
const get = (url, { params, signal, headers } = {}) => handle(apiClient.get(url, { params, signal, headers }));
const del = (url, { params, signal, headers } = {}) => handle(apiClient.delete(url, { params, signal, headers }));
const post = (url, body, { signal, headers } = {}) => handle(apiClient.post(url, body, { signal, headers }));
const put = (url, body, { signal, headers } = {}) => handle(apiClient.put(url, body, { signal, headers }));
const patch = (url, body, { signal, headers } = {}) => handle(apiClient.patch(url, body, { signal, headers }));

const getContentUrl = (path) => {
  const base = (apiClient.defaults.baseURL || '').toLowerCase();
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  if (base.includes('/api')) {
    return `/content${cleanPath}`;
  }
  return `/api/content${cleanPath}`;
};


export const api = {
  auth: {
    completeAccountSetup: data => post('/auth/account-setup',data),
    login: (credentials, opts) => post('/auth/login', credentials, opts),
    logout: (opts) => post('/auth/logout', null, opts),
    profile: (opts) => get('/auth/profile', opts),
    register: (data, opts) => post('/auth/register', data, opts),
    verifyRequest: (email, opts) => post('/auth/verify-request', { email }, opts),
    resetPassword: (data, opts) => post('/auth/reset-password', data, opts),
    forgotPassword: (email, opts) => post('/auth/forgot-password', { email }, opts),
  },

  users: {
    prepareDistributionAccount: data => post('/distribution/accounts/prepare',data),
    sendDistributionAccessMail: id => post(`/distribution/accounts/${id}/access-mail`,{}),
    approveCredit: (id, data, opts) => post(`/users/${id}/credit-approvals`, data, opts),
    list: (params = {}, opts = {}) => get('/users', { params, ...opts }),
    get: (id, opts) => get(`/users/${id}`, opts),
    update: (id, updates, opts) => put(`/users/${id}`, updates, opts),         // admin-only på serveren
    delete: (id, opts) => del(`/users/${id}`, opts),                  // admin-only på serveren
    deleteMe: (opts = {}) => del('/users/me', opts),
    getMe: (opts) => get('/users/me', opts),
    updateProfile: (updates, opts) => put('/users/profile', updates, opts),
    bankInfoGet: (opts) => get('/users/bank-info', opts),
    bankInfoSet: (bankAccountOrPayload, bankReg, opts) => {
      const payload = bankAccountOrPayload && typeof bankAccountOrPayload === 'object' && !Array.isArray(bankAccountOrPayload)
        ? bankAccountOrPayload
        : { bankAccount: bankAccountOrPayload, bankReg };
      return put('/users/bank-info', payload, opts);
    },
    // Aliases used by some components
    getBillingInfo: (opts) => get('/users/billing-info', opts),
    getMyBillingChange: (opts) => get('/users/billing-change/mine', opts),
    createBillingChange: (data, opts) => post('/users/billing-change', data, opts),
    getBankInfo: (opts) => get('/users/bank-info', opts),
    updateBankInfo: (payload = {}, opts) => {
      const body = payload && typeof payload === 'object' && !Array.isArray(payload)
        ? payload
        : { bankAccount: payload, bankReg: undefined };
      return put('/users/bank-info', body, opts);
    },
  },

  savedAddresses: {
    list: (opts = {}) => get('/users/saved-addresses', opts),
    create: (data, opts = {}) => post('/users/saved-addresses', data, opts),
    remove: (id, opts = {}) => del(`/users/saved-addresses/${id}`, opts),
    update: (id, data, opts = {}) => put(`/users/saved-addresses/${id}`, data, opts),
  },
  bookingSubmissions: {
    request: data => submissionRequest(apiClient.post('/booking-submissions',data)),
    // Also bypass negative responses cached before the server no-store fix.
    status: id => submissionRequest(apiClient.get(`/booking-submissions/${id}`,{params:{_fresh:Date.now()}})),
    verify: (id,code) => submissionRequest(apiClient.post(`/booking-submissions/${id}/verify`,{code})),
  },

  recentAddresses: {
    list: (opts = {}) => get('/users/recent-addresses', opts),
  },

  depots: {
    list: (opts = {}) => get('/depots', opts),
    getFleetVehicles: (params, opts = {}) => get('/depots/vehicles', { params, ...opts }),
  },

  admin: {
    searchDispatchTasks: (params = {}, opts = {}) => get('/admin/dispatch/tasks', { params, ...opts }),
    // counts (hvis UI bruger dem)
    pendingTasksCount: (opts) => get('/admin/tasks/pending-count', opts),
    pendingCarriersCount: (opts) => get('/admin/carriers/pending-count', opts),
    blockedUsersCount: (opts) => get('/admin/users/blocked-count', opts),
    pendingManualReviewsCount: (opts) => get('/admin/reviews/pending-count', opts),
    // approvals / blocks
    approveUser: (userId, opts) => put(`/admin/users/${userId}/approve`, null, opts),
    rejectUser: (userId, opts) => put(`/admin/users/${userId}/reject`, null, opts),
    blockUser: (userId, opts) => put(`/admin/users/${userId}/block`, null, opts),
    unblockUser: (userId, opts) => put(`/admin/users/${userId}/unblock`, null, opts),

    // billing changes (profil-/fakturainfo ændringer fra carrier)
    getBillingChanges: (status, opts = {}) => {
      const params = status ? { status } : {};
      return get('/admin/billing-changes', { params, ...opts });
    },
    approveBillingChange: (id, opts) => put(`/admin/billing-changes/${id}/approve`, null, opts),
    rejectBillingChange: (id, opts) => put(`/admin/billing-changes/${id}/reject`, null, opts),

    // carrier lists (valgfrit, hvis brugt i UI)
    getCarrierLists: (opts) => get('/admin/carrier-lists', opts),
    createCarrierList: (data, opts) => post('/admin/carrier-lists', data, opts),
    updateCarrierList: (listId, data, opts) => put(`/admin/carrier-lists/${listId}`, data, opts),
    deleteCarrierList: (listId, opts) => del(`/admin/carrier-lists/${listId}`, opts),
    getFinancialOverview: (opts) => get('/admin/financial/overview', opts),
    getPlatformSaldo: (opts) => get('/admin/financial/platform-saldo', opts),
    getPaymentBookings: (opts = {}) => get('/admin/payments/bookings', opts),
    markPaymentPaid: (data, opts = {}) => post('/admin/payments/bookings/mark-paid', data, opts),
    cancelPaymentBooking: (data, opts = {}) => post('/admin/payments/bookings/cancel', data, opts),
    approvePaymentCancellation: (data, opts = {}) => post('/admin/payments/bookings/approve-cancellation', data, opts),
    refundPaymentBooking: (data, opts = {}) => post('/admin/payments/bookings/refund', data, opts),
    payCarrier: async (data, opts = {}) => {
      const res = await post('/admin/payments/bookings/pay-carrier', data, opts);
      await trackPayoutResult(res);
      return res;
    },
    getInstantQuoteSettings: (opts) => get('/admin/settings/instant-quote', opts),
    updateInstantQuoteSettings: (data, opts) => put('/admin/settings/instant-quote', data, opts),
    dispatchAssign: (data, opts) => post('/admin/dispatch/assign', data, opts),
    taskTypes: {
      list: (opts) => get('/admin/task-types', opts),
      update: (type, data, opts) => put(`/admin/task-types/${type}`, data, opts),
      delete: (type, opts) => del(`/admin/task-types/${type}`, opts),
    },
    // transport matrix
    getTransportProfile: (opts = {}) => get('/admin/transport-profile', opts),
    updateTransportProfile: (data, opts = {}) => put('/admin/transport-profile', data, opts),
    updateTransportBands: (rows, opts = {}) => put('/admin/transport-profile/bands', rows, opts),
    updateTransportSurcharges: (rows, opts = {}) => put('/admin/transport-profile/surcharges', rows, opts),
    updateTransportPackages: (rows, opts = {}) => put('/admin/transport-profile/packages', rows, opts),
    // fleet management
    fleet: {
      listVehicles: (opts = {}) => get('/admin/fleet/vehicles', opts),
      createVehicle: (data, opts = {}) => post('/admin/fleet/vehicles', data, opts),
      updateVehicle: (id, data, opts = {}) => patch(`/admin/fleet/vehicles/${id}`, data, opts),
      deleteVehicle: (id, opts = {}) => del(`/admin/fleet/vehicles/${id}`, opts),
      listReservations: (opts = {}) => get('/admin/fleet/reservations', opts),
    },
    depots: {
      list: (opts = {}) => get('/admin/depots', opts),
      create: (data, opts = {}) => post('/admin/depots', data, opts),
      update: (id, data, opts = {}) => patch(`/admin/depots/${id}`, data, opts),
      delete: (id, opts = {}) => del(`/admin/depots/${id}`, opts),
    },
    contactMessages: {
      list: (opts = {}) => get('/contact/admin', opts),
      markRead: (id, isRead, opts = {}) => patch(`/contact/admin/${id}/read`, { is_read: isRead }, opts),
      delete: (id, opts = {}) => del(`/contact/admin/${id}`, opts),
    },
  },

  finance: {
    // Carrier / admin self overview
    getMy: (opts = {}) => get('/finance/me', opts),
    getStatementMe: (opts = {}) => get('/finance/statement/me', opts),
    getOrder: (type, id, opts = {}) => get(`/finance/order/${type}/${id}`, opts),
    // Admin ledger search
    getLedger: (params = {}, opts = {}) => get('/finance/ledger', { params, ...opts }),
    getTaskEconomy: (taskId, opts = {}) => get(`/finance/task/${taskId}`, opts),
    // Payout helpers
    startStripeOnboarding: (opts = {}) => post('/finance/stripe/onboarding', null, opts),
    disconnectStripe: (opts = {}) => post('/finance/stripe/disconnect', null, opts),
    createCarrierPayout: (data, opts = {}) => post('/finance/payouts/carrier', data, opts),
    createPlatformPayout: (data, opts = {}) => post('/finance/payouts/platform', data, opts),
    updatePayoutStatus: async (payoutId, data = {}, opts = {}) => {
      const res = await put(`/finance/payouts/${payoutId}/status`, data, opts);
      await trackPayoutResult(res);return res;
    },
    // Adjustments (admin)
    createAdjustment: (data, opts = {}) => post('/finance/adjustments', data, opts),
    updateAdjustmentStatus: (id, data, opts = {}) => put(`/finance/adjustments/${id}/status`, data, opts),
    createCustomerAdjustment: (data, opts = {}) => post('/finance/customer-adjustments', data, opts),
  },

  platformSettings: {
    get: (opts) => get('/platform-settings', opts),
    update: (updates, opts) => put('/platform-settings', updates, opts),
  },

  notifications: {
    list: (params = {}, opts = {}) => get('/notifications', { params, ...opts }),
    unreadCount: (opts = {}) => get('/notifications/unread-count', opts),
    create: (data, opts = {}) => post('/notifications', data, opts),
    // Aliases: old naming (markRead) + new naming (markAsRead)
    markRead: (id, opts = {}) => patch(`/notifications/${id}/read`, null, opts),
    markAllRead: (opts = {}) => patch('/notifications/read-all', null, opts),
    markAsRead: (id, opts = {}) => patch(`/notifications/${id}/read`, null, opts),
    markAllAsRead: (opts = {}) => patch('/notifications/read-all', null, opts),
    delete: (id, opts = {}) => del(`/notifications/${id}`, opts),
  },

  // eksempler (kun hvis UI bruger dem)
  ratings: {
    list: (params = {}, opts = {}) => get('/ratings', { params, ...opts }),
    summary: (params = {}, opts = {}) => get('/ratings/summary', { params, ...opts }),
    byCarrier: (carrierId, opts) => get(`/ratings/carrier/${carrierId}`, opts),
    byTask: (taskId, opts = {}) => get(`/ratings/task/${taskId}`, opts),
    create: (data, opts = {}) => post('/ratings', data, opts),
    mine: (params = {}, opts = {}) => get('/ratings/mine', { params, ...opts }),
    reply: (id, reply, opts = {}) => patch(`/ratings/${id}/reply`, { reply }, opts),
    adminList: (params = {}, opts = {}) => get('/ratings/admin', { params, ...opts }),
    adminCreate: (data, opts = {}) => post('/ratings/admin', data, opts),
    approve: (id, opts = {}) => patch(`/ratings/${id}/approve`, {}, opts),
    reject: (id, opts = {}) => patch(`/ratings/${id}/reject`, {}, opts),
  },
  reviewRequests: {
    create: (data, opts = {}) => post('/ratings/review-requests', data, opts),
    list: (params = {}, opts = {}) => get('/ratings/review-requests', { params, ...opts }),
    resolve: (id, action, opts = {}) => patch(`/ratings/review-requests/${id}/resolve`, { action }, opts),
  },

  // Stats wrapper (maps to /api/stats)
  stats: {
    get: (opts = {}) => get('/stats', opts),
  },

  pricing: {
    quote: (data, opts = {}) => post('/pricing/quote', data, opts),
    transportQuote: (data, opts = {}) => post('/pricing/transport-quote', data, opts),
    calculate: (data, opts = {}) => post('/pricing/calculate', data, opts),
  },

  payments: {
    // Server mounts both /payments and /api/payments; with baseURL '/api' this works
    createIntent: (data, opts = {}) => post('/payments/create-intent', data, opts),
    createExtraIntent: (data, opts = {}) => post('/payments/extra-intent', data, opts),
    createTopupIntent: (data, opts = {}) => post('/payments/create-topup-intent', data, opts),
    confirm: async (paymentIntentId, opts = {}) => {
      const res=await post('/payments/confirm', { paymentIntentId }, opts);
      await trackBookingResult(res, transactionId => handle(apiClient.post('/analytics/booking-claim', {transactionId, statisticsConsent: true}, {timeout: 2000})));return res;
    },
    creditPay: async (data, opts = {}) => {
      const res=await post('/payments/credit-pay', data, opts);
      await trackBookingResult(res, transactionId => handle(apiClient.post('/analytics/booking-claim', {transactionId, statisticsConsent: true}, {timeout: 2000})));return res;
    },
  },

  insurance: {
    // Carrier-side helpers
    getMine: (opts = {}) => get('/insurance', opts),
    create: (data, opts = {}) => post('/insurance', data, opts),
    upload: (formData, opts = {}) =>
      handle(apiClient.post('/insurance/upload', formData, {
        ...opts,
        headers: { ...(opts?.headers || {}) },
      })),
    update: (id, data, opts = {}) => put(`/insurance/${id}`, data, opts),
    getByUserId: (userId, opts = {}) => get('/insurance', { params: { userId }, ...opts }),
    // Admin overview (requires dedicated backend route if not already present)
    adminList: (params = {}, opts = {}) => get('/insurance/admin', { params, ...opts }),
    adminUploads: (userId, opts = {}) => get(`/insurance/admin/${userId}/uploads`, opts),
  },

  reservations: {
    create: (data, opts = {}) => post('/reservations', data, opts),
  },

  booking: {
    slots: (params = {}, opts = {}) => get('/booking/slots', { params, ...opts }),
    reserve: (data = {}, opts = {}) => post('/booking/reserve', data, opts),
    createPaymentIntent: (taskId, data = {}, opts = {}) => post(`/booking/${taskId}/payment-intent`, data, opts),
    capture: (taskId, opts = {}) => post(`/booking/${taskId}/capture`, null, opts),
    cancel: (taskId, opts = {}) => post(`/booking/${taskId}/cancel`, null, opts),
    adminList: (params = {}, opts = {}) => get('/booking/admin/list', { params, ...opts }),
    reschedule: (taskId, data = {}, opts = {}) => post(`/booking/${taskId}/reschedule`, data, opts),
    assignCarrier: (taskId, data = {}, opts = {}) => post(`/booking/${taskId}/assign-carrier`, data, opts),
  },

  extra: {
    listByInstantQuote: (id, opts = {}) => get(`/extra/instant-quotes/${id}/extra-charges`, opts),
    allocate: (id, allocation, opts = {}) => post(`/extra/extra-charges/${id}/allocation`, { allocation }, opts),
    listByTask: (taskId, opts = {}) => get(`/extra/tasks/${taskId}/extra-charges`, opts),
    createForTask: (taskId, data, opts = {}) => post(`/extra/tasks/${taskId}/extra-charges`, data, opts),
    approve: (id, opts = {}) => post(`/extra/extra-charges/${id}/approve`, null, opts),
    reject: (id, opts = {}) => post(`/extra/extra-charges/${id}/reject`, null, opts),
    cancel: (id, opts = {}) => post(`/extra/extra-charges/${id}/cancel`, null, opts),
    adminList: (params = {}, opts = {}) => get('/extra/admin/extra-charges', { params, ...opts }),
  },

  tasks: {
    list: (params = {}, opts = {}) => get('/tasks', { params, ...opts }),
    get: (id, opts = {}) => get(`/tasks/${id}`, opts),
    create: async (data, opts = {}) => {
      const res = await post('/tasks', data, opts);
      if (res && (res.id || res.task_id)) {
        await pushTrackingEvent('task_created', {
          task_id: `task:${res.id || res.task_id}`,
          task_type: res.type,
          booking_type: 'regular',
        });
      }
      return res;
    },
    update: (id, data, opts = {}) => put(`/tasks/${id}`, data, opts),
    updateStatus: (id, data, opts = {}) => patch(`/tasks/${id}/status`, data, opts),
    addProof: (id, data, opts = {}) => post(`/tasks/${id}/proofs`, data, opts),
    getBids: (id, opts = {}) => get(`/tasks/${id}/bids`, opts),
    assign: (id, data, opts = {}) => post(`/tasks/${id}/assign`, data, opts),
    listDeletionRequests: (id, opts = {}) => get(`/tasks/${id}/deletion-requests`, opts),
    resolveDeletionRequest: (id, rid, action = 'approve', opts = {}) =>
      patch(`/tasks/${id}/deletion-requests/${rid}`, { action }, opts),
    deleteProof: (taskId, proofId, opts = {}) =>
      del(`/tasks/${taskId}/proofs/${proofId}`, opts),
    delete: (id, opts = {}) => del(`/tasks/${id}`, opts),
  },

  drafts: {
    get: (opts = {}) => get('/drafts/current', opts),
    save: (data, opts = {}) => post('/drafts/current', { data }, opts),
    clear: (opts = {}) => del('/drafts/current', opts),
  },

  bids: {
    // Optional list endpoint (might be 404 if not implemented server-side)
    list: (params = {}, opts = {}) => get('/bids', { params, ...opts }),
    get: (id, opts = {}) => get(`/bids/${id}`, opts),
    create: async (data, opts = {}) => {
      const res = await post('/bids', data, opts);
      const createdBid = res?.bid || res;
      if (createdBid && (createdBid.id || createdBid.bid_id)) {
        await pushTrackingEvent('bid_sent', {
          task_id: `task:${createdBid.task_id || createdBid.taskId}`,
          bid_id: `bid:${createdBid.id || createdBid.bid_id}`,
          value: decimalAnalyticsValue(createdBid.price),
          currency: 'DKK',
        });
      }
      return createdBid;
    },
    update: (id, data, opts = {}) => patch(`/bids/${id}`, data, opts),
    extend: (id, days = 14, opts = {}) => put(`/bids/${id}/extend`, { days }, opts),
  },

  instantQuotes: {
    proposeTime: (id,data) => patch(`/instant-quotes/${id}/time-proposal`,data),
    acceptTime: (id,data) => post(`/instant-quotes/${id}/accept-time`,data),
    list: (params = {}, opts = {}) => get('/instant-quotes', { params, ...opts }),
    get: (id, opts = {}) => get(`/instant-quotes/${id}`, opts),
    create: async (data, opts = {}) => {
      const res = await post('/instant-quotes', data, opts);
      if (res && (res.id || res.task_id)) {
        await pushTrackingEvent('task_created', {
          task_id: `instant_quote:${res.id || res.task_id}`,
          task_type: res.type,
          booking_type: 'instant',
        });
      }
      return res;
    },
    update: (id, data, opts = {}) => patch(`/instant-quotes/${id}`, data, opts),
    approve: (id, data = {}, opts = {}) => patch(`/instant-quotes/${id}/approve`, data, opts),
    assignSlot: (id, data, opts = {}) => patch(`/instant-quotes/${id}/assign-slot`, data, opts),
    addProof: (id, data, opts = {}) => post(`/instant-quotes/${id}/proofs`, data, opts),
  },

  chat: {
    unreadCount: (opts = {}) => get('/chat2/unread', opts),
    listThread: (taskId, u1, u2, opts = {}) => get('/chat2', { params: { task_id: taskId, u1, u2 }, ...opts }),
    sendMessage: (taskId, receiverId, message, opts = {}) => post('/chat2', { task_id: taskId, receiver_id: receiverId, message }, opts),
    markRead: (chatId, opts = {}) => patch(`/chat2/${chatId}/read`, null, opts),
  },

  upload: {
    image: (formData, opts = {}) => handle(apiClient.post('/upload/image', formData, {
      ...opts,
      headers: { ...(opts?.headers || {}) },
    })),
    document: (formData, opts = {}) => handle(apiClient.post('/upload/document', formData, {
      ...opts,
      headers: { ...(opts?.headers || {}) },
    })),
  },

  freightNotes: {
    get: (taskId, opts = {}) => get(`/freight-notes/${taskId}`, opts),
    update: (taskId, data, opts = {}) => put(`/freight-notes/${taskId}`, data, opts),
    submit: (taskId, data = {}, opts = {}) => post(`/freight-notes/${taskId}/submit`, data, opts),
    approve: (taskId, data = {}, opts = {}) => post(`/freight-notes/${taskId}/approve`, data, opts),
  },

  actions: {
    summary: (opts = {}) => get('/actions/summary', opts),
    remindMe: (opts = {}) => post('/actions/remind-me', null, opts),
  },

  calendar: {
    instantAvailability: (params) => get('/calendar/instant-availability', {params}),
    getConfigs: (opts = {}) => get('/calendar/configs', opts),
    getConfig: (opts = {}) => get('/calendar/config', opts),
    updateConfig: (data, opts = {}) => post('/calendar/config', data, opts),
    deleteConfig: (id, opts = {}) => del(`/calendar/config/${id}`, opts),
    getSlots: (params, opts = {}) => get('/calendar/slots', { params, ...opts }),
    getSlotReservations: (params, opts = {}) => get('/calendar/slot-reservations', { params, ...opts }),
  },
  config: {
    getGlobalConfig: (opts = {}) => get('/config/global', opts),
    getPackages: (opts = {}) => get('/config/packages', opts),
    getHelperMappings: (opts = {}) => get('/config/helper-mapping', opts),
    getVehiclePricing: (opts = {}) => get('/config/vehicle-pricing', opts),
    getSurchargeConfig: (opts = {}) => get('/config/surcharge', opts),
    updateSurchargeConfig: (data, opts = {}) => post('/config/surcharge', data, opts),
    getPricingDetails: (params, opts = {}) => get('/config/pricing-details', { params, ...opts }),
    getPriceMatrix: (opts = {}) => get('/config/price-matrix', opts),
  },
  routing: {
    getRouteDetails: (data, opts = {}) => post('/route', data, opts),
    getAutocomplete: (query, opts = {}) => post('/route/autocomplete', { query }, opts),
    getPlaceDetails: (placeId, opts = {}) => post('/route/place-details', { placeId }, opts),
  },
  content: {
    getBySlug: (slug, opts = {}) => get(getContentUrl(`/${slug}`), opts),
    getAdminList: (params = {}, opts = {}) => get(getContentUrl('/admin'), { params, ...opts }),
    getAdminById: (id, opts = {}) => get(getContentUrl(`/admin/${id}`), opts),
    saveDraft: (id, data, opts = {}) => post(getContentUrl(`/admin/${id}/draft`), data, opts),
    publish: (id, data, opts = {}) => post(getContentUrl(`/admin/${id}/publish`), data, opts),
    unpublish: (id, opts = {}) => post(getContentUrl(`/admin/${id}/unpublish`), null, opts),
    create: (data, opts = {}) => post(getContentUrl(''), data, opts),
    update: (id, data, opts = {}) => put(getContentUrl(`/${id}`), data, opts),
    delete: (id, opts = {}) => del(getContentUrl(`/${id}`), opts),
    revalidate: (path, opts = {}) => post(getContentUrl('/revalidate'), { path }, opts),
    // Chat Integration (Phase 1) — singleton
    getChatIntegration: (opts = {}) => get(getContentUrl('/admin/chat-integration'), opts),
    saveChatIntegration: (data, opts = {}) => post(getContentUrl('/admin/chat-integration'), data, opts),
    // Chat Integrations (CRUD) — multi-record
    chatIntegrations: {
      list:   (opts = {}) => get(getContentUrl('/admin/chat-integrations'), opts),
      getById:(id, opts = {}) => get(getContentUrl(`/admin/chat-integrations/${id}`), opts),
      create: (data, opts = {}) => post(getContentUrl('/admin/chat-integrations'), data, opts),
      update: (id, data, opts = {}) => put(getContentUrl(`/admin/chat-integrations/${id}`), data, opts),
      remove: (id, opts = {}) => del(getContentUrl(`/admin/chat-integrations/${id}`), opts),
    },
  },
  templates: {
    list: (opts = {}) => get('/templates', opts),
    get: (id, opts = {}) => get(`/templates/${id}`, opts),
    create: (data, opts = {}) => post('/templates', data, opts),
    update: (id, data, opts = {}) => put(`/templates/${id}`, data, opts),
    delete: (id, opts = {}) => del(`/templates/${id}`, opts),
  },
  siteSettings: {
    homepage: (opts = {}) => get('/site-settings/homepage', opts),
    homepageOptions: (after = 0, opts = {}) => get(`/admin/site-settings/homepage-options?after=${after}`, opts),
    saveHomepage: (data, opts = {}) => put('/admin/site-settings/homepage', data, opts),
    list: (opts = {}) => get('/admin/site-settings', opts),
    create: (data, opts = {}) => post('/admin/site-settings', data, opts),
    update: (id, data, opts = {}) => put(`/admin/site-settings/${id}`, data, opts),
  },
};

export default api;
